import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { KNOWN_GESTURES } from "../engines/gestureEngine";
import type { CustomGesture } from "../actions/customGestures";
import {
  averageTemplates,
  deleteCustomGesture,
  newCustomGestureId,
  normalizeHand,
  slugGestureName,
  upsertCustomGesture,
  DEFAULT_CUSTOM_THRESHOLD,
  type Point2,
} from "../actions/customGestures";

type Props = {
  liveOn: boolean;
  lastHandRef: MutableRefObject<Point2[] | null>;
  gestures: CustomGesture[];
  onChange: (list: CustomGesture[]) => void;
  onTaught: (name: string) => void;
  onDeleted: (name: string) => void;
};

const HOLD_MS = 1600;

export function TeachGestureCard({
  liveOn,
  lastHandRef,
  gestures,
  onChange,
  onTaught,
  onDeleted,
}: Props) {
  const [name, setName] = useState("");
  const [phase, setPhase] = useState<"idle" | "holding" | "error">("idle");
  const [hint, setHint] = useState("");
  const samplesRef = useRef<number[][][]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const collectingRef = useRef(false);

  useEffect(() => {
    if (phase !== "holding") return;
    const id = window.setInterval(() => {
      if (!collectingRef.current) return;
      const pts = lastHandRef.current;
      if (!pts) return;
      const norm = normalizeHand(pts);
      if (norm) samplesRef.current.push(norm);
    }, 40);
    return () => window.clearInterval(id);
  }, [phase, lastHandRef]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const finish = () => {
    collectingRef.current = false;
    const samples = samplesRef.current;
    samplesRef.current = [];
    let slug = slugGestureName(name);
    if ((KNOWN_GESTURES as readonly string[]).includes(slug)) slug = `Custom_${slug}`;
    if (samples.length < 8) {
      setPhase("error");
      setHint("Keep a hand in frame and hold still, then try again.");
      return;
    }
    const template = averageTemplates(samples);
    if (!template) {
      setPhase("error");
      setHint("Could not build a pose from those frames.");
      return;
    }
    const g: CustomGesture = {
      id: newCustomGestureId(),
      name: slug,
      template,
      threshold: DEFAULT_CUSTOM_THRESHOLD,
      createdAt: Date.now(),
    };
    onChange(upsertCustomGesture(g));
    onTaught(slug);
    setPhase("idle");
    setHint(`Saved “${slug.replace(/_/g, " ")}” — assign an action below.`);
    setName("");
  };

  const startHold = () => {
    const slug = slugGestureName(name);
    if (!liveOn) {
      setPhase("error");
      setHint("Start the camera first.");
      return;
    }
    if (!slug || slug === "Custom" && !name.trim()) {
      setPhase("error");
      setHint("Name the gesture (e.g. Rock on).");
      return;
    }
    samplesRef.current = [];
    collectingRef.current = true;
    setPhase("holding");
    setHint("Hold the pose still…");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(finish, HOLD_MS);
  };

  const remove = (g: CustomGesture) => {
    onChange(deleteCustomGesture(g.id));
    onDeleted(g.name);
  };

  return (
    <section className="vf-card">
      <div className="vf-label">Teach a gesture</div>
      <div className="vf-cost-hint" style={{ marginBottom: 8 }}>
        Hold a pose the canned model does not know (rock on, three fingers…). In-browser
        only — no training upload.
      </div>
      <input
        className="vf-input"
        style={{ width: "100%", marginBottom: 8 }}
        placeholder="Name (Rock on)"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={phase === "holding"}
      />
      <div className="vf-row">
        <button
          type="button"
          className="vf-btn-primary"
          disabled={phase === "holding"}
          onClick={startHold}
        >
          {phase === "holding" ? "Holding…" : "Hold to teach"}
        </button>
      </div>
      {hint && (
        <div className={phase === "error" ? "vf-error" : "vf-success"} style={{ marginTop: 6 }}>
          {hint}
        </div>
      )}
      {gestures.length > 0 && (
        <div className="vf-custom-list" style={{ marginTop: 10 }}>
          {gestures.map((g) => (
            <div key={g.id} className="vf-custom-recipe">
              <div>
                <strong>★ {g.name.replace(/_/g, " ")}</strong>
                <div className="vf-binding-hint">Taught pose</div>
              </div>
              <button type="button" className="vf-chip" onClick={() => remove(g)}>
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
